import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  CheckboxGrid,
  Field,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getOtDischargeNoteForm,
  updateOtDischargeNoteForm,
} from "../../../../../services/patientService";
import {
  AlertCircleIcon,
  BrainIcon,
  CommentsIcon,
  FileCheckIcon,
  GraduationCapIcon,
  HeartPulseIcon,
  HomeIcon,
  LogOutIcon,
  MedicalNotesIcon,
  RunningIcon,
  TargetIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  OT_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import PainScalePills from "../ptNotes/PainScalePills";
import {
  AFFECTED_SIDE_OPTIONS,
  ASSIST_LEVEL_OPTIONS,
  DISCHARGE_REASON_OPTIONS,
  EDUCATION_TOPIC_OPTIONS,
  FOLLOW_UP_OPTIONS,
  FUNCTIONAL_TASKS,
  GOAL_STATUS_OPTIONS,
  HEP_FLAG_OPTIONS,
  ORIENTATION_OPTIONS,
  OT_DISCHARGE_ARRAY_KEYS,
  OT_GOAL_ROWS,
  OT_INTERVENTION_OPTIONS,
  PAIN_FREQUENCY_OPTIONS,
  PROBLEM_SOLVING_OPTIONS,
  STATUS_DISCHARGE_OPTIONS,
  STATUS_METRICS,
  VISUAL_PERCEPTUAL_OPTIONS,
  emptyOtDischargePayload,
  mergeFunctionalAssessment,
  mergeGoalStatuses,
  mergeStatusComparisonRows,
  mergeUeRomRows,
  toggleInList,
  today,
} from "./otDischargeNoteContent";
import {
  assertOtVisitMetaForFlag,
  buildOtVisitMetaProps,
  pickOtVisitMeta,
} from "./otVisitCommon";
import OtNotesCommonHeader from "./OtNotesCommonHeader";
import {
  normalizeOtCommonHeaderState,
  resolveOtNotesCommonHeader,
  withNormalizedOtCommonHeader,
} from "./otNotesCommonHeaderUtils";

const DISCHARGE_DATE_MAX = new Date().toISOString().slice(0, 10);
const OFFICE_FLAGS = new Set(["send_to_office", "to_be_sign"]);

const normalizePainLevel = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isInteger(n) && n >= 0 && n <= 10 ? n : null;
};

const normalizeExclusive = (value, options) => {
  if (Array.isArray(value)) {
    return options.find((opt) => value.includes(opt)) || "";
  }
  const s = String(value || "").trim();
  return options.includes(s) ? s : "";
};

const tableHeadClass =
  "bg-slate-50 text-[9px] font-semibold uppercase tracking-wide text-slate-500";

const SelectField = ({
  label,
  name,
  value,
  options,
  canEdit,
  printMode,
  onChange,
  placeholder = "Select…",
}) => (
  <Field label={label}>
    {printMode ? (
      <div className={`${fieldClass} flex items-center`}>{value || "—"}</div>
    ) : (
      <select
        name={name}
        className={fieldClass}
        value={value || ""}
        disabled={!canEdit}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    )}
  </Field>
);

SelectField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

const PillToggle = ({ name, options, value, canEdit, onChange }) => (
  <div
    className={`grid gap-1.5 ${
      options.length === 3 ? "grid-cols-3" : "grid-cols-2"
    }`}
  >
    {options.map((opt) => {
      const selected = value === opt;
      return (
        <button
          key={opt}
          type="button"
          name={name}
          disabled={!canEdit}
          onClick={() => onChange(selected ? "" : opt)}
          className={`h-7 rounded-md border text-[12px] font-semibold transition-colors ${
            selected
              ? "border-indigo-600 bg-indigo-600 text-white"
              : "border-slate-200 bg-white text-slate-700 hover:border-indigo-300"
          } ${!canEdit ? "cursor-default opacity-80" : "cursor-pointer"}`}
          aria-pressed={selected}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

PillToggle.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

const emptyValues = () => ({
  ...emptyOtDischargePayload(),
  dischargeDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});

const OtDischargeNoteForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient?.lastName, patient?.firstName],
  );

  const buildPatientPrefill = useCallback(() => {
    const episodes = Array.isArray(patient?.episodes) ? patient.episodes : [];
    const latestEpisode = episodes[0];
    const soc = latestEpisode?.socDate || latestEpisode?.startDate || "";
    return {
      ...emptyOtDischargePayload(),
      socDate: soc ? formatDateForInput(soc) : "",
    };
  }, [patient]);

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const base = emptyOtDischargePayload();
      const loadedPayload =
        data.payload && typeof data.payload === "object" && !Array.isArray(data.payload)
          ? data.payload
          : {};

      const merged = {
        ...base,
        ...loadedPayload,
        ...pickOtVisitMeta(loadedPayload, data),
        dischargeDate:
          data.dischargeDate || loadedPayload.dischargeDate || today(),
        socDate: String(loadedPayload.socDate || base.socDate || "").trim(),
        commonHeader: normalizeOtCommonHeaderState(
          loadedPayload.commonHeader || base.commonHeader,
        ),
        dischargeReasonOther: String(loadedPayload.dischargeReasonOther || "").trim(),
        subjectiveReport: String(loadedPayload.subjectiveReport || "").trim(),
        statusComparison: mergeStatusComparisonRows(loadedPayload.statusComparison),
        functionalAssessment: mergeFunctionalAssessment(
          loadedPayload.functionalAssessment,
        ),
        orientation: normalizeExclusive(loadedPayload.orientation, ORIENTATION_OPTIONS),
        problemSolving: normalizeExclusive(
          loadedPayload.problemSolving,
          PROBLEM_SOLVING_OPTIONS,
        ),
        visualPerceptual: normalizeExclusive(
          loadedPayload.visualPerceptual,
          VISUAL_PERCEPTUAL_OPTIONS,
        ),
        affectedSide: normalizeExclusive(
          loadedPayload.affectedSide,
          AFFECTED_SIDE_OPTIONS,
        ),
        ueRomStrength: mergeUeRomRows(loadedPayload.ueRomStrength),
        painLevel: normalizePainLevel(loadedPayload.painLevel),
        painFrequency: normalizeExclusive(
          loadedPayload.painFrequency,
          PAIN_FREQUENCY_OPTIONS,
        ),
        goalStatuses: mergeGoalStatuses(loadedPayload.goalStatuses),
        goalComments: String(loadedPayload.goalComments || "").trim(),
        exerciseFrequency: String(loadedPayload.exerciseFrequency || "").trim(),
        followUpRecommendation: normalizeExclusive(
          loadedPayload.followUpRecommendation,
          FOLLOW_UP_OPTIONS,
        ),
        communicationNotes: String(loadedPayload.communicationNotes || "").trim(),
        dischargeSummary: String(loadedPayload.dischargeSummary || "").trim(),
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      };

      OT_DISCHARGE_ARRAY_KEYS.forEach((key) => {
        merged[key] = Array.isArray(loadedPayload[key])
          ? loadedPayload[key].map(String)
          : Array.isArray(base[key])
            ? base[key]
            : [];
      });

      setValues(merged);
      resetSignatures();
    },
    [patientName, resetSignatures],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolveOtNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName ||
              draft?.formName ||
              "Occupational Therapy Discharge Note",
            formType:
              clinicalForm.formType || draft?.formType || "ot_discharge_note",
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            dischargeDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: { ...buildPatientPrefill(), commonHeader },
          });
          return;
        }

        const res = await getOtDischargeNoteForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const commonHeader = await resolveOtNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: { ...existingPayload, commonHeader },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleListValue = (key, item) => {
    setValues((prev) => ({
      ...prev,
      [key]: toggleInList(prev[key], item),
    }));
  };

  const updateStatusRow = (metric, patch) => {
    setValues((prev) => ({
      ...prev,
      statusComparison: (prev.statusComparison || []).map((row) =>
        row.metric === metric ? { ...row, ...patch } : row,
      ),
    }));
  };

  const updateFunctional = (taskKey, level) => {
    setValues((prev) => ({
      ...prev,
      functionalAssessment: {
        ...(prev.functionalAssessment || {}),
        [taskKey]: level,
      },
    }));
  };

  const updateUeRow = (joint, patch) => {
    setValues((prev) => ({
      ...prev,
      ueRomStrength: (prev.ueRomStrength || []).map((row) =>
        row.joint === joint ? { ...row, ...patch } : row,
      ),
    }));
  };

  const setGoalStatus = (goal, status) => {
    setValues((prev) => ({
      ...prev,
      goalStatuses: {
        ...prev.goalStatuses,
        [goal]: status,
      },
    }));
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        OT_FORM_EDITOR_ROLES,
        "View only — only OT/OTA can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType:
      form?.formType ||
      draft?.formType ||
      clinicalForm?.formType ||
      "ot_discharge_note",
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId:
      form?.filledBy || clinicalForm?.filledBy || null,
  });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      dischargeDate,
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      ...otFields
    } = values;

    const nestedPayload = withNormalizedOtCommonHeader({
      ...otFields,
      dischargeDate: dischargeDate ? formatDateForInput(dischargeDate) : "",
      socDate: values.socDate ? formatDateForInput(values.socDate) : "",
      statusComparison: mergeStatusComparisonRows(values.statusComparison),
      functionalAssessment: mergeFunctionalAssessment(values.functionalAssessment),
      ueRomStrength: mergeUeRomRows(values.ueRomStrength),
      goalStatuses: mergeGoalStatuses(values.goalStatuses),
      orientation: normalizeExclusive(values.orientation, ORIENTATION_OPTIONS),
      problemSolving: normalizeExclusive(
        values.problemSolving,
        PROBLEM_SOLVING_OPTIONS,
      ),
      visualPerceptual: normalizeExclusive(
        values.visualPerceptual,
        VISUAL_PERCEPTUAL_OPTIONS,
      ),
      affectedSide: normalizeExclusive(values.affectedSide, AFFECTED_SIDE_OPTIONS),
      painLevel: normalizePainLevel(values.painLevel),
      painFrequency: normalizeExclusive(values.painFrequency, PAIN_FREQUENCY_OPTIONS),
      followUpRecommendation: normalizeExclusive(
        values.followUpRecommendation,
        FOLLOW_UP_OPTIONS,
      ),
    });

    OT_DISCHARGE_ARRAY_KEYS.forEach((key) => {
      nestedPayload[key] = Array.isArray(values[key]) ? values[key] : [];
    });

    const payload = {
      flag,
      dischargeDate: dischargeDate ? formatDateForInput(dischargeDate) : null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    if (OFFICE_FLAGS.has(flag)) {
      const hasReason =
        (Array.isArray(values.dischargeReasons) &&
          values.dischargeReasons.length > 0) ||
        Boolean(String(values.dischargeReasonOther || "").trim());
      if (!hasReason) {
        toast.error("Select a reason for OT discharge, or specify Other.");
        return;
      }
      if (!String(values.dischargeSummary || "").trim()) {
        toast.error("Clinical justification & case summary is required.");
        return;
      }
    }
    const visitMetaError = assertOtVisitMetaForFlag(values, flag);
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateOtDischargeNoteForm,
        filledByUserId,
        scheduleDate: formatDateForInput(values.dischargeDate) || today(),
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);
  const functional = values.functionalAssessment || {};

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            Occupational Therapy Discharge Note
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">
            Occupational Therapy | Clinical Documentation System
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          caregiverSlot={caregiverSlot}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth ||
              values.commonHeader?.dateOfBirth ||
              form.patientDateOfBirth,
          )}
          dateLabel="Discharge Date"
          dateValue={displayDateUS(values.dischargeDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400">
                  Discharge Date
                </div>
                <InputDatePicker
                  name="dischargeDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.dischargeDate) || ""}
                  onChange={(event) =>
                    setValue("dischargeDate", event.target.value)
                  }
                  max={DISCHARGE_DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildOtVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <OtNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />

        <Section icon={AlertCircleIcon} title="7. Reason for OT Discharge">
          <CheckboxGrid
            options={DISCHARGE_REASON_OPTIONS}
            values={values.dischargeReasons}
            canEdit={canEdit}
            onToggle={(opt) => toggleListValue("dischargeReasons", opt)}
            columns={2}
          />
          <div className="mt-3">
            <Field label="Other">
              <textarea
                rows={2}
                className={textareaClass}
                value={values.dischargeReasonOther || ""}
                disabled={!canEdit}
                placeholder="Specify other reason for discharge..."
                onChange={(e) => setValue("dischargeReasonOther", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={CommentsIcon} title="8. Subjective Report">
          <Field label="Patient/Caregiver Statement on Progress">
            <textarea
              rows={4}
              className={textareaClass}
              value={values.subjectiveReport || ""}
              disabled={!canEdit}
              placeholder="Enter patient's subjective comments regarding their functional progress and satisfaction with OT services..."
              onChange={(e) => setValue("subjectiveReport", e.target.value)}
            />
          </Field>
        </Section>

        <Section icon={FileCheckIcon} title="9. Patient Status at Discharge">
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="min-w-full text-left text-[11px]">
              <thead>
                <tr className={tableHeadClass}>
                  <th className="px-2.5 py-2 w-[28%]">Metric</th>
                  <th className="px-2.5 py-2">Admission Status</th>
                  <th className="px-2.5 py-2">Discharge Status</th>
                </tr>
              </thead>
              <tbody>
                {STATUS_METRICS.map((metric) => {
                  const row = (values.statusComparison || []).find(
                    (item) => item.metric === metric,
                  ) || { metric, admissionStatus: "", dischargeStatus: "" };
                  const dischargeOptions =
                    STATUS_DISCHARGE_OPTIONS[metric] || ASSIST_LEVEL_OPTIONS;
                  return (
                    <tr key={metric} className="border-t border-slate-100">
                      <td className="px-2.5 py-1.5 font-medium text-slate-700 whitespace-nowrap">
                        {metric}
                      </td>
                      <td className="px-1.5 py-1">
                        {printMode ? (
                          <div className={`${fieldClass} flex items-center`}>
                            {row.admissionStatus || "—"}
                          </div>
                        ) : (
                          <input
                            type="text"
                            className={fieldClass}
                            value={row.admissionStatus || ""}
                            disabled={!canEdit}
                            placeholder="Admission status"
                            onChange={(e) =>
                              updateStatusRow(metric, {
                                admissionStatus: e.target.value,
                              })
                            }
                          />
                        )}
                      </td>
                      <td className="px-1.5 py-1">
                        {printMode ? (
                          <div className={`${fieldClass} flex items-center`}>
                            {row.dischargeStatus || "—"}
                          </div>
                        ) : (
                          <select
                            className={fieldClass}
                            value={row.dischargeStatus || ""}
                            disabled={!canEdit}
                            onChange={(e) =>
                              updateStatusRow(metric, {
                                dischargeStatus: e.target.value,
                              })
                            }
                          >
                            <option value="">Select…</option>
                            {dischargeOptions.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>

        <Section icon={HomeIcon} title="10. Functional Assessment">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
            {FUNCTIONAL_TASKS.map((task) => (
              <SelectField
                key={task.key}
                label={task.label}
                name={`functional-${task.key}`}
                value={functional[task.key] || ""}
                options={ASSIST_LEVEL_OPTIONS}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(opt) => updateFunctional(task.key, opt)}
              />
            ))}
          </div>
        </Section>

        <Section icon={BrainIcon} title="11. Cognition &amp; Perception Assessment">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 items-end">
            <Field label="Orientation">
              <PillToggle
                name="orientation"
                options={ORIENTATION_OPTIONS}
                value={values.orientation || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("orientation", opt)}
              />
            </Field>
            <SelectField
              label="Problem Solving"
              name="problemSolving"
              value={values.problemSolving || ""}
              options={PROBLEM_SOLVING_OPTIONS}
              canEdit={canEdit}
              printMode={printMode}
              onChange={(opt) => setValue("problemSolving", opt)}
            />
            <SelectField
              label="Visual/Perceptual"
              name="visualPerceptual"
              value={values.visualPerceptual || ""}
              options={VISUAL_PERCEPTUAL_OPTIONS}
              canEdit={canEdit}
              printMode={printMode}
              onChange={(opt) => setValue("visualPerceptual", opt)}
            />
          </div>
        </Section>

        <Section icon={RunningIcon} title="12. Upper Extremity ROM &amp; Strength">
          <div className="rounded-md border border-indigo-100 bg-indigo-50/40 overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-2 px-2.5 py-2 border-b border-indigo-100 bg-indigo-50/80">
              <SubHeading>Affected Side</SubHeading>
              <div className="min-w-[18rem] flex-1 max-w-xl">
                <PillToggle
                  name="affectedSide"
                  options={AFFECTED_SIDE_OPTIONS}
                  value={values.affectedSide || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("affectedSide", opt)}
                />
              </div>
            </div>
            <div className="overflow-x-auto bg-white">
              <table className="min-w-full text-left text-[11px]">
                <thead>
                  <tr className={tableHeadClass}>
                    <th className="px-2.5 py-2 w-[22%]">Joint Action</th>
                    <th className="px-2.5 py-2">ROM (Discharge)</th>
                    <th className="px-2.5 py-2">MMT (Discharge)</th>
                    <th className="px-2.5 py-2">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {(values.ueRomStrength || []).map((row) => (
                    <tr key={row.joint} className="border-t border-slate-100">
                      <td className="px-2.5 py-1.5 font-medium text-slate-700 whitespace-nowrap">
                        {row.joint}
                      </td>
                      <td className="px-1.5 py-1">
                        <input
                          type="text"
                          className={fieldClass}
                          value={row.rom || ""}
                          disabled={!canEdit}
                          placeholder={
                            row.joint === "Hand Grip (lbs)" ? "e.g. 45 lbs" : "e.g. 150°"
                          }
                          onChange={(e) =>
                            updateUeRow(row.joint, { rom: e.target.value })
                          }
                        />
                      </td>
                      <td className="px-1.5 py-1">
                        <input
                          type="text"
                          className={fieldClass}
                          value={row.mmt || ""}
                          disabled={!canEdit}
                          placeholder={
                            row.joint === "Hand Grip (lbs)" ? "N/A" : "e.g. 4/5"
                          }
                          onChange={(e) =>
                            updateUeRow(row.joint, { mmt: e.target.value })
                          }
                        />
                      </td>
                      <td className="px-1.5 py-1">
                        <input
                          type="text"
                          className={fieldClass}
                          value={row.notes || ""}
                          disabled={!canEdit}
                          onChange={(e) =>
                            updateUeRow(row.joint, { notes: e.target.value })
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Section>

        <Section icon={HeartPulseIcon} title="13. Pain Assessment">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 items-stretch">
            <div className="lg:col-span-2 min-w-0 h-full">
              <PainScalePills
                label="Current Pain Level (0–10)"
                name="painLevel"
                value={values.painLevel}
                canEdit={canEdit}
                onChange={(v) => setValue("painLevel", v)}
              />
            </div>
            <div className="rounded-md border border-slate-200 bg-slate-50/40 px-2.5 py-2 h-full flex flex-col justify-center">
              <SelectField
                label="Pain Frequency"
                name="painFrequency"
                value={values.painFrequency || ""}
                options={PAIN_FREQUENCY_OPTIONS}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(opt) => setValue("painFrequency", opt)}
              />
            </div>
          </div>
        </Section>

        <Section icon={TargetIcon} title="14. OT Goals Status">
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="min-w-full text-left text-[11px]">
              <thead>
                <tr className={tableHeadClass}>
                  <th className="px-2.5 py-2 w-[36%]">Goal</th>
                  {GOAL_STATUS_OPTIONS.map((opt) => (
                    <th key={opt} className="px-2.5 py-2 text-center">
                      {opt}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {OT_GOAL_ROWS.map((goal) => (
                  <tr key={goal} className="border-t border-slate-100">
                    <td className="px-2.5 py-2 font-medium text-slate-700">
                      {goal}
                    </td>
                    {GOAL_STATUS_OPTIONS.map((opt) => {
                      const checked = values.goalStatuses?.[goal] === opt;
                      return (
                        <td key={opt} className="px-2.5 py-2 text-center">
                          <input
                            type="radio"
                            name={`ot-goal-status-${goal}`}
                            className="h-3.5 w-3.5 accent-indigo-600"
                            checked={checked}
                            disabled={!canEdit}
                            onChange={() => {
                              if (!canEdit) return;
                              setGoalStatus(goal, opt);
                            }}
                            onClick={(e) => {
                              if (canEdit && checked) {
                                e.preventDefault();
                                setGoalStatus(goal, "");
                              }
                            }}
                            aria-label={`${goal} ${opt}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3">
            <Field label="Additional Comments">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.goalComments || ""}
                disabled={!canEdit}
                placeholder="Enter clinician observations regarding goal attainment..."
                onChange={(e) => setValue("goalComments", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={MedicalNotesIcon} title="15. Skilled OT Interventions Completed">
          <CheckboxGrid
            options={OT_INTERVENTION_OPTIONS}
            values={values.interventionsCompleted}
            canEdit={canEdit}
            onToggle={(opt) => toggleListValue("interventionsCompleted", opt)}
            columns={2}
          />
        </Section>

        <Section icon={HomeIcon} title="16. Home Exercise Program (HEP)">
          <CheckboxGrid
            options={HEP_FLAG_OPTIONS}
            values={values.hepFlags}
            canEdit={canEdit}
            onToggle={(opt) => toggleListValue("hepFlags", opt)}
            columns={2}
          />
          <div className="mt-3">
            <Field label="Exercise Frequency">
              <input
                type="text"
                className={fieldClass}
                value={values.exerciseFrequency || ""}
                disabled={!canEdit}
                placeholder="e.g., 2x daily, 10 reps of each shoulder mobilization"
                onChange={(e) => setValue("exerciseFrequency", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={GraduationCapIcon} title="17. Education Provided">
          <CheckboxGrid
            options={EDUCATION_TOPIC_OPTIONS}
            values={values.educationTopics}
            canEdit={canEdit}
            onToggle={(opt) => toggleListValue("educationTopics", opt)}
            columns={2}
          />
        </Section>

        <Section icon={LogOutIcon} title="18. Discharge Plan">
          <SelectField
            label="Follow-up Recommendations"
            name="followUpRecommendation"
            value={values.followUpRecommendation || ""}
            options={FOLLOW_UP_OPTIONS}
            canEdit={canEdit}
            printMode={printMode}
            onChange={(opt) => setValue("followUpRecommendation", opt)}
          />
        </Section>

        <Section icon={CommentsIcon} title="19. Physician / Care Team Communication">
          <textarea
            rows={4}
            className={textareaClass}
            value={values.communicationNotes || ""}
            disabled={!canEdit}
            placeholder="Summary of communication with MD or nursing staff regarding discharge findings and final recommendations..."
            onChange={(e) => setValue("communicationNotes", e.target.value)}
          />
        </Section>

        <Section icon={MedicalNotesIcon} title="20. OT Discharge Summary Narrative">
          <Field label="Clinical Justification &amp; Case Summary (Required)">
            <textarea
              rows={7}
              className={textareaClass}
              value={values.dischargeSummary || ""}
              disabled={!canEdit}
              placeholder="Provide a detailed clinical narrative of the patient's course of treatment, specific skilled interventions used, functional gains made from admission to discharge, and justification for ending the episode of care..."
              onChange={(e) => setValue("dischargeSummary", e.target.value)}
            />
          </Field>
        </Section>

        <FormSignatureSections
          {...signatureProps}
          showPatientSection
          patientNameValue={values.patientSignatureName}
          onPatientNameChange={(v) => setValue("patientSignatureName", v)}
          onPatientSign={signatures.handlePatientSign}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
          clinicianSectionTitle="OT Authentication"
          patientSectionTitle="Patient / Caregiver (Acknowledgment of Visit)"
        />

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      {(permissions.canSendToOffice ||
        permissions.canToBeSign ||
        permissions.canReview ||
        permissions.canEdit) && (
        <FormActionBar
          permissions={permissions}
          status={form.status}
          canEdit={canEdit}
          saving={saving}
          onSubmit={submit}
          printMode={printMode}
        />
      )}
    </div>
  );
};

OtDischargeNoteForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default OtDischargeNoteForm;
